// useRollupEditor.js

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  buildRollupDefaultTitle,
  createDefaultRollupDraft,
  getRollupAggregateOptions,
  getRollupFieldLabel,
  normalizeRollupDefinition,
} from 'rollups/rollupDefinitions';

const normalizeString = (value) => String(value || '').trim();

function buildFieldOptions(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map((field) => {
      const key = normalizeString(field?.name || field?.key);
      if (!key) return null;

      const aggregates = getRollupAggregateOptions(field);
      if (!aggregates.length) return null;

      return {
        aggregates,
        field,
        key,
        label: getRollupFieldLabel(field, key),
      };
    })
    .filter(Boolean);
}

function resolveOpenedDraft({
  draft,
  fields,
  rollup,
  titleTouched,
  typeKey,
}) {
  const fieldOptions = buildFieldOptions(fields);
  const fieldMap = new Map(fieldOptions.map((option) => [option.key, option.field]));
  const baseDraft = rollup
    ? normalizeRollupDefinition(rollup, { typeKey })
    : (draft || createDefaultRollupDraft({ fields, typeKey }));
  const nextFieldDef = fieldMap.get(baseDraft.fieldKey) || fieldOptions[0]?.field || null;
  const nextAggregateOptions = getRollupAggregateOptions(nextFieldDef);
  const nextAggregate = nextAggregateOptions.some((option) => option.value === baseDraft.aggregate)
    ? baseDraft.aggregate
    : (nextAggregateOptions[0]?.value || baseDraft.aggregate);

  return {
    ...baseDraft,
    aggregate: nextAggregate,
    fieldKey: normalizeString(baseDraft.fieldKey || nextFieldDef?.name || nextFieldDef?.key),
    title: titleTouched
      ? normalizeString(baseDraft.title)
      : (normalizeString(baseDraft.title) || buildRollupDefaultTitle({
        aggregate: nextAggregate,
        fieldDef: nextFieldDef,
      })),
  };
}

/**
 * Shared controller for RollupEditorForm in modal and Studio surfaces.
 */
export default function useRollupEditor({
  draft: controlledDraft,
  fields = [],
  isOpen = true,
  onDraftChange,
  rollup = null,
  typeKey = null,
} = {}) {
  const isControlled = controlledDraft !== undefined && typeof onDraftChange === 'function';
  const [internalDraft, setInternalDraft] = useState(() => (
    createDefaultRollupDraft({ fields, typeKey })
  ));
  const [titleTouched, setTitleTouched] = useState(false);

  const fieldOptions = useMemo(() => buildFieldOptions(fields), [fields]);

  const fieldMap = useMemo(() => {
    const map = new Map();
    fieldOptions.forEach((option) => {
      map.set(option.key, option.field);
    });
    return map;
  }, [fieldOptions]);

  const draft = isControlled ? controlledDraft : internalDraft;

  const setDraft = useCallback((updater) => {
    if (isControlled) {
      const nextDraft = typeof updater === 'function'
        ? updater(controlledDraft)
        : updater;
      onDraftChange(nextDraft);
      return;
    }

    setInternalDraft(updater);
  }, [controlledDraft, isControlled, onDraftChange]);

  useEffect(() => {
    if (!isOpen) return;

    if (isControlled) {
      const hasResolvableField = normalizeString(controlledDraft?.fieldKey)
        && fieldOptions.some((option) => option.key === normalizeString(controlledDraft?.fieldKey));
      if (hasResolvableField || !fieldOptions.length) return;

      const nextDraft = resolveOpenedDraft({
        draft: controlledDraft,
        fields,
        titleTouched,
        typeKey,
      });

      onDraftChange({
        ...nextDraft,
        key: normalizeString(controlledDraft?.key) || nextDraft.key,
      });
      return;
    }

    const nextDraft = resolveOpenedDraft({
      fields,
      rollup,
      titleTouched,
      typeKey,
    });

    setInternalDraft(nextDraft);
    setTitleTouched(Boolean(normalizeString(nextDraft.title)));
  }, [controlledDraft, fieldMap, fieldOptions, fields, isControlled, isOpen, onDraftChange, rollup, titleTouched, typeKey]);

  const selectedFieldDef = fieldMap.get(draft?.fieldKey) || fieldOptions[0]?.field || null;
  const aggregateOptions = useMemo(
    () => getRollupAggregateOptions(selectedFieldDef),
    [selectedFieldDef],
  );
  const usesCustomColor = draft?.valueColor && draft.valueColor !== 'auto';

  const handleDraftChange = useCallback((patch) => {
    setDraft((prev) => ({
      ...(prev || {}),
      ...(patch || {}),
    }));
  }, [setDraft]);

  const handleFieldChange = useCallback((nextFieldKey) => {
    const nextFieldDef = fieldMap.get(nextFieldKey) || null;
    const nextAggregateOptions = getRollupAggregateOptions(nextFieldDef);
    const nextAggregate = nextAggregateOptions.some((option) => option.value === draft?.aggregate)
      ? draft.aggregate
      : (nextAggregateOptions[0]?.value || 'count_non_empty');

    handleDraftChange({
      aggregate: nextAggregate,
      fieldKey: nextFieldKey,
      title: titleTouched
        ? draft?.title || ''
        : buildRollupDefaultTitle({
          aggregate: nextAggregate,
          fieldDef: nextFieldDef,
        }),
    });
  }, [draft?.aggregate, draft?.title, fieldMap, handleDraftChange, titleTouched]);

  const handleAggregateChange = useCallback((nextAggregate) => {
    handleDraftChange({
      aggregate: nextAggregate,
      title: titleTouched
        ? draft?.title || ''
        : buildRollupDefaultTitle({
          aggregate: nextAggregate,
          fieldDef: selectedFieldDef,
        }),
    });
  }, [draft?.title, handleDraftChange, selectedFieldDef, titleTouched]);

  const handleTitleChange = useCallback((nextTitle) => {
    setTitleTouched(true);
    handleDraftChange({ title: nextTitle });
  }, [handleDraftChange]);

  return {
    aggregateOptions,
    draft,
    editorProps: {
      aggregateOptions,
      draft,
      fieldOptions,
      onAggregateChange: handleAggregateChange,
      onDraftChange: handleDraftChange,
      onFieldChange: handleFieldChange,
      onTitleChange: handleTitleChange,
      usesCustomColor,
    },
    fieldOptions,
    handleAggregateChange,
    handleDraftChange,
    handleFieldChange,
    handleTitleChange,
    selectedFieldDef,
    setTitleTouched,
    usesCustomColor,
  };
}
